import { useCallback, useEffect, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import Card from '@zenbusiness/zui/Card';
import Divider from '@zenbusiness/zui/Divider';
import ErrorState from '@zenbusiness/zui/ErrorState';
import PageContainer from '@zenbusiness/zui/PageContainer';
import PageHeader from '@zenbusiness/zui/PageHeader';
import Text from '@zenbusiness/zui/Text';

import { useAccountSettings } from '#client/hooks/useAccountSettings';
import { isSocialAuth0Id } from '#client/utils/isSocialAuth0Id';

import { AccountHolder } from './components/AccountHolder';
import { ConnectWithPartners } from './components/ConnectWithPartners';
import { ManagePasswordModal } from './components/ManagePasswordModal';
import { OptOutModal } from './components/OptOutModal';

import { MODAL_PARAM_KEY } from '../Home/constants';

function AccountSettings() {
  const { data: accountInfo, error, loading, refetch } = useAccountSettings();

  const [searchParams, setSearchParams] = useSearchParams();
  const [currentModal, setCurrentModal] = useState(null);
  const { allPartnerOptOut, userAuth0Id } = accountInfo;
  const isSocialLogin = isSocialAuth0Id(userAuth0Id);

  const handleOpenModal = useCallback(
    (modal) => {
      setCurrentModal(modal);
      setSearchParams((params) => {
        params.set(MODAL_PARAM_KEY, modal);
        return params;
      });
    },
    [setSearchParams],
  );

  const handleCloseModal = () => {
    setCurrentModal(null);
    setSearchParams((params) => {
      params.delete(MODAL_PARAM_KEY);
      return params;
    });
  };

  useEffect(() => {
    const modalName = searchParams.get(MODAL_PARAM_KEY);
    if (!modalName || currentModal) {
      return;
    }
    handleOpenModal(modalName);
  }, [currentModal, handleOpenModal, searchParams]);

  if (error) {
    return <ErrorState />;
  }

  return (
    <PageContainer>
      <PageHeader
        title={
          <Text component="p" size="2xl" family="secondary" weight="bold">
            Account settings
          </Text>
        }
        sx={{ marginBlockEnd: 3 }}
      />
      <Card sx={{ padding: { xs: 3, sm: 4 } }}>
        <AccountHolder
          isSocialLogin={isSocialLogin}
          account={accountInfo}
          loading={loading}
          onClickEdit={() => handleOpenModal('managePassword')}
        />
        <Divider sx={{ margin: '2rem 0' }} />
        <ConnectWithPartners account={accountInfo} loading={loading} onClickOptOut={() => handleOpenModal('optOut')} />
        <ManagePasswordModal
          account={accountInfo}
          isOpen={currentModal === 'managePassword'}
          onClose={() => handleCloseModal('managePassword')}
        />
        {!allPartnerOptOut && (
          <OptOutModal
            isOpen={currentModal === 'optOut'}
            onClose={() => handleCloseModal('optOut')}
            refresh={refetch}
          />
        )}
      </Card>
    </PageContainer>
  );
}

export default AccountSettings;
